import { FC, RefObject } from "react";

import { useTheme } from "@mui/material";
import { getTextColorOfHexColor, Locales, OrganizationCountry } from "@simplyk/common";
import { UseFormReturn } from "react-hook-form";

import { NewsletterFormQuestion, NewsletterFormSignupData } from "./types";

import { NewsletterFormFields } from "./NewsletterFormFields";
import { NewsletterFormSignUpLayout } from "./NewsletterFormSignUpLayout";

import { useTranslate } from "@/common/hooks/useTranslate";
import { DashboardButton } from "@/dashboard-design-system/DashboardButton";
import { PhoneNumberInputHandle } from "@/dashboard-design-system/PhoneNumberInput/types";
import { DashboardTypography } from "@/dashboard-design-system/Typography/DashboardTypography";

export const NewsletterFormSignUpStateless: FC<{
  mainColor: string;
  buttonColor: string;
  title: string;
  submitState: "submitted" | "submitting" | "idle";
  isAwaitingConfirmation?: boolean;
  form: UseFormReturn<NewsletterFormSignupData>;
  organization: { name: string; country: OrganizationCountry; region?: string | null };
  isEmbedded?: boolean;
  previewConfiguration?: {
    isPreview: boolean;
  };
  hasFirstName?: boolean;
  hasLastName?: boolean;
  hasPhoneNumber?: boolean;
  hasAddress?: boolean;
  hasLanguage?: boolean;
  hasCommunicationPreferences?: boolean;
  isLegacyCommunicationPreferences?: boolean;
  communicationPreferencesTitle?: string | null;
  communicationPreferences?: Array<{
    id: string;
    label: string;
    contactListId: string;
  }>;
  phoneNumberInputRef?: RefObject<PhoneNumberInputHandle | null>;
  phoneInputKey?: number;
  questions?: NewsletterFormQuestion[];
  locale: Locales;
}> = ({
  mainColor,
  buttonColor,
  title,
  submitState,
  isAwaitingConfirmation,
  form,
  organization,
  isEmbedded,
  previewConfiguration,
  hasFirstName,
  hasLastName,
  hasPhoneNumber,
  hasAddress,
  hasLanguage,
  hasCommunicationPreferences,
  isLegacyCommunicationPreferences,
  communicationPreferencesTitle,
  communicationPreferences = [],
  phoneNumberInputRef,
  phoneInputKey,
  questions,
  locale,
}) => {
  const { t } = useTranslate();
  const mainTextColor = getTextColorOfHexColor(mainColor);
  const buttonTextColor = getTextColorOfHexColor(buttonColor);
  const theme = useTheme();

  return (
    <NewsletterFormSignUpLayout
      isEmbedded={isEmbedded}
      backgroundColor={mainColor}
      slots={{
        title: (
          <DashboardTypography variant="h4" sx={{ color: mainTextColor }}>
            {title}
          </DashboardTypography>
        ),
        fields: (
          <NewsletterFormFields
            form={form}
            organization={organization}
            previewConfiguration={previewConfiguration}
            hasFirstName={hasFirstName}
            hasLastName={hasLastName}
            hasPhoneNumber={hasPhoneNumber}
            hasAddress={hasAddress}
            hasLanguage={hasLanguage}
            hasCommunicationPreferences={hasCommunicationPreferences}
            isLegacyCommunicationPreferences={isLegacyCommunicationPreferences}
            communicationPreferencesTitle={communicationPreferencesTitle}
            communicationPreferences={communicationPreferences}
            phoneNumberInputRef={phoneNumberInputRef}
            phoneInputKey={phoneInputKey}
            questions={questions}
            locale={locale}
          />
        ),
        submitButton: (
          <DashboardButton
            variant="filled"
            fullWidth
            type="submit"
            size="large"
            data-test="form-submit-button"
            // The submitted state only changes the button's label and color, so expose it for assertions
            data-submit-state={submitState}
            isLoading={submitState === "submitting"}
            disabled={previewConfiguration?.isPreview}
            style={
              previewConfiguration?.isPreview
                ? {
                    backgroundColor: buttonColor,
                    color: buttonTextColor,
                    cursor: "initial",
                    opacity: 1,
                    pointerEvents: "none",
                  }
                : {
                    backgroundColor: submitState === "submitted" ? theme.palette.success.dark : buttonColor,
                    color: submitState === "submitted" ? theme.palette.success.contrastText : buttonTextColor,
                  }
            }
          >
            {submitState === "submitted"
              ? t(
                  "dashboard",
                  isAwaitingConfirmation
                    ? "newsletterForms.signUp.awaitingConfirmation"
                    : "newsletterForms.signUp.submitted"
                )
              : t("common", "Submit")}
          </DashboardButton>
        ),
      }}
    />
  );
};
